import React, { useState, useRef, useEffect } from 'react';
import Navbar from './Navbar';
import Card3D from './Card3D';
import './App.css';

function App() {
  const paragraphs = [
    "music went digital and fans lost something real.",
    "album cards bring back the ritual of collecting without the hassle of outdated tech.",
    "tap to play, hold to keep, trade with friends, collect to support."
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const isScrolling = useRef(false);
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      
      if (isScrolling.current) return;
      
      // Only proceed if scroll is significant enough
      if (Math.abs(e.deltaY) < 50 && Math.abs(e.deltaX) < 50) return;
      
      isScrolling.current = true;
      
      // Determine direction based on which delta is larger
      const isVertical = Math.abs(e.deltaY) > Math.abs(e.deltaX);
      const delta = isVertical ? e.deltaY : e.deltaX;
      
      if (delta > 0 && currentIndex < paragraphs.length - 1) {
        setCurrentIndex(prev => prev + 1);
      } else if (delta < 0 && currentIndex > 0) {
        setCurrentIndex(prev => prev - 1);
      }
      
      setTimeout(() => {
        isScrolling.current = false;
      }, 1000);
    };

    const handleTouchStart = (e: TouchEvent) => {
      touchStart.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY
      };
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!touchStart.current || isScrolling.current) return;

      const touchEnd = {
        x: e.changedTouches[0].clientX,
        y: e.changedTouches[0].clientY
      };

      const deltaX = touchStart.current.x - touchEnd.x;
      const deltaY = touchStart.current.y - touchEnd.y;

      // Minimum swipe distance threshold
      const minSwipeDistance = 50;

      if (Math.abs(deltaX) > minSwipeDistance || Math.abs(deltaY) > minSwipeDistance) {
        isScrolling.current = true;

        // Determine if horizontal or vertical swipe
        if (Math.abs(deltaY) > Math.abs(deltaX)) {
          // Vertical swipe
          if (deltaY > 0 && currentIndex < paragraphs.length - 1) {
            // Swipe up - next paragraph
            setCurrentIndex(prev => prev + 1);
          } else if (deltaY < 0 && currentIndex > 0) {
            // Swipe down - previous paragraph
            setCurrentIndex(prev => prev - 1);
          }
        } else {
          // Horizontal swipe
          if (deltaX > 0 && currentIndex < paragraphs.length - 1) {
            // Swipe left - next paragraph
            setCurrentIndex(prev => prev + 1);
          } else if (deltaX < 0 && currentIndex > 0) {
            // Swipe right - previous paragraph
            setCurrentIndex(prev => prev - 1);
          }
        }

        setTimeout(() => {
          isScrolling.current = false;
        }, 500);
      }

      touchStart.current = null;
    };

    // Add event listeners to the document/window for entire page scrolling
    document.addEventListener('wheel', handleWheel, { passive: false });
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    
    return () => {
      document.removeEventListener('wheel', handleWheel);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [currentIndex, paragraphs.length]);

  return (
    <div className="App home-page">
      <Navbar />
      <div style={{ 
        position: 'relative',
        width: '100%',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 20px 20px 20px',
        boxSizing: 'border-box',
        zIndex: 10
      }}>
        <div style={{
          width: '100%',
          maxWidth: '800px',
          height: '400px',
          marginBottom: '20px'
        }}>
          <Card3D slideIndex={currentIndex} isLastSlide={currentIndex === paragraphs.length - 1} />
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          maxWidth: '800px'
        }}>
          <div className="scroll-container" ref={containerRef} style={{
            position: 'relative',
            transform: 'none',
            width: '100%',
            textAlign: 'center',
            height: 'auto',
            minHeight: '100px',
            padding: '0'
          }}>
            {paragraphs.map((paragraph, index) => (
              <div
                key={index}
                className={`paragraph-snap ${index === currentIndex ? 'active' : ''}`}
                style={{
                  position: index === 0 ? 'relative' : 'absolute',
                  padding: '10px 20px',
                  height: 'auto'
                }}
              >
                <p className="paragraph-text">{paragraph}</p>
              </div>
            ))}
          </div>
          <div className="scroll-indicator" style={{
            marginTop: '-10px',
            animation: currentIndex < paragraphs.length - 1 ? 'bounce 2s infinite' : 'none',
            cursor: 'pointer',
            opacity: currentIndex < paragraphs.length - 1 ? 1 : 0,
            height: '30px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
